import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { UUIDPathRegex } from '@cbgms/base/utils/uuid'
import { getRepairTimeChapterName } from 'app/workorder/state/workorder/selectors'
import { Breadcrumbs, Crumb } from '@carsys/ui'
import { splitRegistration } from '@cbgms/base/utils/vehicle'

export const VehicleBreadCrumbs: React.FC<React.ComponentProps<typeof Breadcrumbs>> = ({ ...props }) => {
  const { t } = useTranslation()
  const invoice = useSelector(state => state.workorder.invoice.Workorder)
  const repairTimeChapters = useSelector(state => state.workorder.repairTimes.chapters)
  const fullVehicleRegistration = useSelector(state => state.vehicle?.detail?.Registration || '')
  const { licensePlate: vehicleRegistration } = useMemo(() => splitRegistration(fullVehicleRegistration), [fullVehicleRegistration])

  return (
    <Breadcrumbs {...props}>
      <Crumb key='crumb-vehicles' data-testid='crumb-vehicles' path='/vehicles' text={t('vehicle.vehicles')} />
      <Crumb
        key='crumb-vehicles-quick-create'
        data-testid='crumb-vehicles-quick-create'
        path='/vehicles/quick-create'
        text={`${t('vehicle.quick_create_title')}`}
      />
      <Crumb
        key='crumb-vehicles-create-by-vin'
        data-testid='crumb-vehicles-create-by-vin'
        path='/vehicles/quick-create-by-vin'
        text={`${t('vehicle.quick_create_title')}`}
      />
      <Crumb
        key='crumb-vehicles-uuid'
        data-testid='crumb-vehicles-uuid'
        path={`/vehicles/:vehicleUUID(${UUIDPathRegex})`}
        text={vehicleRegistration ? `${t('vehicle.vehicle')} (${vehicleRegistration})` : t('vehicle.vehicle')}
      />
      <Crumb
        key='crumb-vehicles-print-consult-vehicle'
        data-testid='crumb-vehicles-print-consult-vehicle'
        path='/vehicles/:uuid/apk/:transactionUUID/print/consult-vehicle'
        text={t('APK.ConsultVehicle')}
      />
      <Crumb
        key='crumb-vehicles-print-inspection-report'
        data-testid='crumb-vehicles-print-inspection-report'
        path='/vehicles/:uuid/apk/:apkUUID/print/inspection-report'
        text={t('APK.InspectionResult')}
      />
      <Crumb
        key='crumb-vehicles-invoice'
        data-testid='crumb-vehicles-invoice'
        path='/vehicles/:uuid/invoices/:invoiceUUID'
        text={t('workorder.invoice')}
      />
      <Crumb
        key='crumb-vehicles-account'
        data-testid='crumb-vehicles-account'
        path='/vehicles/:uuid/*accounts/:accountUUID'
        text={t('vehicle.edit_account')}
      />
      <Crumb
        key='crumb-vehicles-haynes-service-schedule'
        data-testid='crumb-vehicles-haynes-service-schedule'
        path='/vehicles/:uuid/*haynes/service-schedule/:conditionUUID?'
        text={t('vehicle.haynes_service_schedule')}
      />
      <Crumb
        key='crumb-vehicles-haynes-service-schedule-interval-data'
        data-testid='crumb-vehicles-haynes-service-schedule-interval-data'
        path='/vehicles/:uuid/*haynes/service-schedule/:conditionUUID?/intervaldata'
        text={t('vehicle.haynes_service_interval_data')}
      />
      <Crumb
        key='crumb-vehicles-haynes-repair-times'
        data-testid='crumb-vehicles-haynes-repair-times'
        path='/vehicles/:uuid/*haynes/repair-times'
        text={t('vehicle.haynes_repair_times')}
      />
      <Crumb
        key='crumb-orders-haynespro-iframe'
        data-testid='crumb-orders-haynespro-iframe'
        path='/vehicles/:uuid/*haynes/frame'
        text={t('workorder.haynes_iframe')}
      />
      <Crumb
        key='crumb-vehicles-haynes-repair-times-node'
        data-testid='crumb-vehicles-haynes-repair-times-node'
        path='/vehicles/:uuid/*haynes/repair-times/:nodeUUID'
        text={params => getRepairTimeChapterName(repairTimeChapters, params.nodeUUID)}
      />
      <Crumb
        key='crumb-history-invoices-view'
        data-testid='crumb-history-invoices-view'
        path='/vehicles/:uuid/history/invoices/:uuid'
        text={`${t('invoice.invoice')} ${invoice.InvoiceNumber}`}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-owner-account'
        key='crumb-workorders-view-link-owner-account'
        path='/vehicles/:uuid/*link-owner-account'
        text={t('vehicle.link_owner_account')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-company-account'
        key='crumb-workorders-view-link-company-account'
        path='/vehicles/:uuid/*link-company-account'
        text={t('vehicle.link_company_account')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-lease-company-account'
        key='crumb-workorders-view-link-lease-company-account'
        path='/vehicles/:uuid/*link-lease-account'
        text={t('vehicle.link_lease_account')}
      />
      <Crumb
        data-testid='crumb-vehicle-road-assisstance'
        key='crumb-vehicle-road-assisstance'
        path='/vehicles/road-assistance'
        text={t('RoadAssistance.title')}
      />
    </Breadcrumbs>
  )
}

export default VehicleBreadCrumbs
